/* eslint-disable no-underscore-dangle */
// 在此读取系统配置

import type { BrowserInitConfig } from '@apmplus/web';
import type { IAnyObject } from '@yimoka/shared';
import { isVacuous, mergeWithArrayOverride } from '@yimoka/shared';

declare global {
  interface Window {
    _qiyun_config?: IAnyObject;
  }
}

/**
 * 区分环境：
 * 本地开发 → 使用相对路径 /api 走 Vite 代理
 * 线上生产 → 使用正式外网接口地址
 */
export const CLIENT_API_BASE = import.meta.env.DEV
  ? '/api'
  : 'https://client.qiyunedu.com/api';

/**
 * 拼接客户端 API 完整 URL
 *
 * @param path - 以 `/` 开头的接口路径（如 `/shared/content/portal/article/list`）
 */
export const clientApiUrl = (path: string): string => {
  const base = CLIENT_API_BASE.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

interface IEnv {
  tenantID: string;
  /** ICUA 官网租户，用于跨租户拉取「ICUA 中心要闻」 */
  icuaTenantID: string;
  courseTenantID?: string;
  confAPI: string;
  mode: string;
  debug: boolean;
  api: string;
  loadRemoteLang?: boolean;
  staticAssetsBase?: string;
  apm?: BrowserInitConfig;
}

// 默认的配置
const defaultEnv: IEnv = {
  tenantID: 'AjzPBYwAABA',
  icuaTenantID: 'AjzO8M0AABA',
  api: CLIENT_API_BASE,
  confAPI: CLIENT_API_BASE,
  mode: '',
  debug: true,
  loadRemoteLang: false,
  staticAssetsBase: 'https://static.huashicloud.com/assets/ka/cipp',
  apm: {
    aid: 0,
    token: '',
    // 忽略 volcvideo.com 域名的资源和请求采集
    plugins: {
      resource: {
        ignoreUrls: [/^https?:\/\/([^/]*\.)?volcvideo\.com/i],
      },
      ajax: {
        ignoreUrls: [/^https?:\/\/([^/]*\.)?volcvideo\.com/i],
      },
      fetch: {
        ignoreUrls: [/^https?:\/\/([^/]*\.)?volcvideo\.com/i],
      },
    },
  },
};
if (import.meta.env.DEV) {
  defaultEnv.loadRemoteLang = true;
}
if (import.meta.env.VITE_COURSE_TENANT_ID) {
  defaultEnv.courseTenantID = String(import.meta.env.VITE_COURSE_TENANT_ID);
}
if (import.meta.env.VITE_ICUA_TENANT_ID) {
  defaultEnv.icuaTenantID = String(import.meta.env.VITE_ICUA_TENANT_ID);
}

export const ENV = !isVacuous(window._qiyun_config)
  ? mergeWithArrayOverride(defaultEnv, window._qiyun_config)
  : defaultEnv;

/**
 * 获取团队静态资源 CDN 地址
 * @param path 相对路径
 */
export const getStaticAssetUrl = (path: string): string => {
  const base = ENV.staticAssetsBase ?? '';
  if (!base) return path;
  const normalizedBase = base.replace(/\/$/, '');
  const normalizedPath = path.replace(/^\//, '');
  return `${normalizedBase}/${normalizedPath}`;
};
